<template>
  <div class="image-preview-wrapper">
    <div 
      v-if="src" 
      class="image-preview-container"
      :class="{ 'is-round': round }"
      :style="{ width: width, height: height }"
      @click="openPreview"
    >
      <img 
        v-if="!hasError" 
        :src="cachedSrc" 
        :alt="alt"
        class="preview-img"
        :style="{ objectFit: fit }"
        @load="handleImageLoad"
        @error="handleImageError"
      />
      <div v-else class="image-error">
        <el-icon><Picture /></el-icon>
        <span>{{ errorText }}</span>
      </div>
    </div>
    <div v-else class="image-placeholder" :style="{ width: width, height: height }">
      <el-icon><Picture /></el-icon>
      <span v-if="showPlaceholderText">{{ placeholderText }}</span>
    </div>

    <Teleport to="body">
      <Transition name="fade">
        <div 
          v-if="visible" 
          class="preview-overlay" 
          @click="closePreview"
          @wheel.prevent="handleWheel"
        >
          <div class="preview-close" @click.stop="closePreview">
            <el-icon><Close /></el-icon>
          </div>
          <div class="preview-actions">
            <div class="preview-title" v-if="previewTitle">{{ previewTitle }}</div>
            <div class="preview-zoom-info">{{ Math.round(scale * 100) }}%</div>
          </div>
          <div 
            class="preview-image-container"
            :style="containerStyle"
            @mousedown="startDrag"
            @click.stop
          >
            <img 
              :src="cachedSrc" 
              class="preview-full-image"
              :style="imageStyle"
              @load="handleImageLoad"
              @error="handleImageError"
              draggable="false"
            />
          </div>
          <div v-if="loading" class="preview-loading">
            <el-icon class="is-loading"><Loading /></el-icon>
          </div>
          <div class="preview-hint">
            <span>Scroll to zoom • Drag to pan</span>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from 'vue'
import { Picture, Close, Loading } from '@element-plus/icons-vue'
import { imageCacheUtil } from '@/utils/image-cache'

const props = defineProps({
  src: {
    type: String,
    default: ''
  },
  width: {
    type: String,
    default: '100px'
  },
  height: {
    type: String,
    default: '100px'
  },
  fit: {
    type: String,
    default: 'cover'
  },
  round: {
    type: Boolean,
    default: false
  },
  enablePreview: {
    type: Boolean,
    default: true
  },
  showPlaceholderText: {
    type: Boolean,
    default: false
  },
  placeholderText: {
    type: String,
    default: 'No Image'
  },
  errorText: {
    type: String,
    default: 'Failed to load'
  },
  alt: {
    type: String,
    default: 'Image'
  },
  previewTitle: {
    type: String,
    default: ''
  },
  fileName: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['error', 'load', 'preview-open', 'preview-close', 'oss-expired'])

const visible = ref(false)
const loading = ref(false)
const hasError = ref(false)
const scale = ref(1)
const translateX = ref(0)
const translateY = ref(0)
const isDragging = ref(false)
const startX = ref(0)
const startY = ref(0)

const cachedSrc = computed(() => {
  console.log('[ImagePreview] Getting src for:', props.src?.substring(0, 60), 'fileName:', props.fileName)
  
  // For OSS signed URLs, check if we have a cached timestamp
  if (props.src && imageCacheUtil.isOssSignedUrl(props.src)) {
    const cacheInfo = imageCacheUtil.getCacheTimestamp(props.src, props.fileName || null)
    
    if (cacheInfo) {
      console.log('[ImagePreview] ✅ Using cached OSS URL (valid until expiry):', props.fileName)
      // Use the original cached URL - it may still be valid or browser will handle it
      return props.src
    }
    
    // No cache info, use URL directly and mark as cached
    console.log('[ImagePreview] First load - caching OSS URL timestamp:', props.fileName)
    imageCacheUtil.setCacheTimestamp(props.src, Date.now(), props.fileName || null)
    return props.src
  }
  
  // For non-OSS URLs, try base64 cache first
  const cachedData = imageCacheUtil.getCachedImage(props.src, props.fileName || null)
  if (cachedData) {
    console.log('[ImagePreview] ✅ Using cached image data (base64) for:', props.fileName)
    return cachedData
  }
  
  console.log('[ImagePreview] ❌ No cached image data, using URL directly')
  return props.src
})

const MIN_SCALE = 0.1
const MAX_SCALE = 5
const ZOOM_STEP = 0.1

const containerStyle = computed(() => ({
  cursor: isDragging.value ? 'grabbing' : 'grab'
}))

const imageStyle = computed(() => ({
  transform: `translate(${translateX.value}px, ${translateY.value}px) scale(${scale.value})`,
  transition: isDragging.value ? 'none' : 'transform 0.2s ease'
}))

watch(() => props.src, (newSrc) => {
  if (newSrc) {
    hasError.value = false
    resetTransform()
  }
})

const resetTransform = () => {
  scale.value = 1
  translateX.value = 0
  translateY.value = 0
}

const openPreview = () => {
  if (!props.enablePreview || !props.src || hasError.value) return
  visible.value = true
  loading.value = true
  resetTransform()
  document.body.style.overflow = 'hidden'
  document.addEventListener('keydown', handleKeydown)
  emit('preview-open')
}

const closePreview = () => {
  visible.value = false
  document.body.style.overflow = ''
  document.removeEventListener('keydown', handleKeydown)
  emit('preview-close')
}

const handleKeydown = (e) => {
  if (e.key === 'Escape') {
    closePreview()
  }
}

const handleImageLoad = async () => {
  loading.value = false
  
  // For OSS URLs, just mark as cached (timestamp tracking)
  if (props.src && imageCacheUtil.isOssSignedUrl(props.src)) {
    console.log('[ImagePreview] ✅ OSS image loaded successfully - timestamp cached:', props.fileName)
    imageCacheUtil.setCacheTimestamp(props.src, Date.now(), props.fileName || null)
  } else if (props.src && !props.src.startsWith('data:') && !props.src.startsWith('blob:')) {
    // For non-OSS URLs, try to cache as base64
    console.log('[ImagePreview] Attempting to cache image data for:', props.fileName || props.src.substring(0, 50))
    const base64Data = await imageCacheUtil.cacheImage(props.src, props.fileName || null)
    if (base64Data) {
      imageCacheUtil.setCachedImage(props.src, base64Data, props.fileName || null)
      console.log('[ImagePreview] ✅ Image cached successfully for:', props.fileName)
    } else {
      console.log('[ImagePreview] ⚠️ Image caching skipped or failed')
    }
  }
  
  emit('load', props.src)
}

const handleImageError = (event) => {
  loading.value = false
  hasError.value = true
  
  // Check if this is an OSS URL with expired signature (403 error)
  if (props.src && imageCacheUtil.isOssSignedUrl(props.src)) {
    console.warn('[ImagePreview] OSS image load failed - signature expired (will use cache on next load):', props.src)
    // Do NOT emit oss-expired event - no need to notify server
    // Cache will be used on next load if available, or user will click refresh
  }
  
  emit('error', props.src, event)
}

const handleWheel = (e) => {
  const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP
  const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale.value + delta))
  
  if (newScale !== scale.value) {
    scale.value = newScale
    
    if (newScale <= 1) {
      translateX.value = 0
      translateY.value = 0
    }
  }
}

const startDrag = (e) => {
  if (scale.value <= 1) return
  
  isDragging.value = true
  startX.value = e.clientX - translateX.value
  startY.value = e.clientY - translateY.value
  
  document.addEventListener('mousemove', onDrag)
  document.addEventListener('mouseup', stopDrag)
}

const onDrag = (e) => {
  if (!isDragging.value) return
  
  const maxX = (scale.value - 1) * window.innerWidth / 2
  const maxY = (scale.value - 1) * window.innerHeight / 2
  
  let newX = e.clientX - startX.value
  let newY = e.clientY - startY.value
  
  newX = Math.max(-maxX, Math.min(maxX, newX))
  newY = Math.max(-maxY, Math.min(maxY, newY))
  
  translateX.value = newX
  translateY.value = newY
}

const stopDrag = () => {
  isDragging.value = false
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
}

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
})
</script>

<style scoped lang="scss">
.image-preview-wrapper {
  display: inline-block;
  position: relative;
}

.image-preview-container {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: #f5f7fa;
  cursor: pointer;
  transition: all 0.3s ease;

  &.is-round {
    border-radius: 50%;
  }

  &:hover {
    opacity: 0.9;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  }

  .preview-img {
    width: 100%;
    height: 100%;
    display: block;
  }

  .image-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #909399;
    padding: 16px;

    .el-icon {
      font-size: 32px;
    }

    span {
      font-size: 12px;
    }
  }
}

.image-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: #f5f7fa;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  color: #909399;

  .el-icon {
    font-size: 24px;
  }

  span {
    font-size: 12px;
  }
}

.preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  user-select: none;

  .preview-close {
    position: absolute;
    top: max(20px, env(safe-area-inset-top));
    right: max(20px, env(safe-area-inset-right));
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.3s ease;
    z-index: 10000;
    
    @media (max-width: 768px) {
      top: max(60px, calc(env(safe-area-inset-top) + 20px));
      right: max(20px, calc(env(safe-area-inset-right) + 10px));
      width: 52px;
      height: 52px;
    }

    .el-icon {
      font-size: 28px;
      color: #fff;
    }

    &:hover {
      background-color: rgba(255, 255, 255, 0.2);
    }
    
    &:active {
      background-color: rgba(255, 255, 255, 0.3);
      transform: scale(0.95);
    }
  }

  .preview-actions {
    position: absolute;
    top: max(20px, env(safe-area-inset-top));
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    z-index: 10000;
    
    @media (max-width: 768px) {
      top: max(60px, calc(env(safe-area-inset-top) + 20px));
    }

    .preview-title {
      color: #fff;
      font-size: 16px;
      background-color: rgba(0, 0, 0, 0.5);
      padding: 8px 16px;
      border-radius: 4px;
    }

    .preview-zoom-info {
      color: #fff;
      font-size: 14px;
      background-color: rgba(0, 0, 0, 0.5);
      padding: 4px 12px;
      border-radius: 4px;
    }
  }

  .preview-image-container {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .preview-full-image {
    max-width: 90vw;
    max-height: 90vh;
    object-fit: contain;
    cursor: grab;
    animation: zoomIn 0.3s ease;
    transform-origin: center center;
    will-change: transform;
  }

  .preview-loading {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;

    .el-icon {
      font-size: 48px;
      color: #fff;
    }
  }

  .preview-hint {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    color: rgba(255, 255, 255, 0.6);
    font-size: 14px;
    background-color: rgba(0, 0, 0, 0.5);
    padding: 8px 16px;
    border-radius: 4px;
  }
}

@keyframes zoomIn {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
